import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";
import {
  LayoutDashboard, Building2, Briefcase, FileText, Receipt,
  CreditCard, Users, Sparkles, ArrowRight, Search,
} from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { db } from "@/os/data";
import type { AppRole } from "@/auth/AuthProvider";

type Props = { role: AppRole };

type Hit = {
  id: string;
  kind: "client" | "project" | "invoice" | "quote";
  label: string;
  sub?: string;
  to: string;
};

export function CommandPalette({ role }: Props) {
  const { t } = useI18n();
  const T = (t as any).os.cmdk;
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  // Cached data (loaded on open, admin only).
  const [hits, setHits] = useState<Hit[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open || loaded || role !== "admin") return;
    (async () => {
      try {
        const [clients, projects, invoices, quotes] = await Promise.all([
          db().listClients().catch(() => []),
          db().listProjects().catch(() => []),
          db().listInvoices().catch(() => []),
          db().listQuotes().catch(() => []),
        ]);
        const out: Hit[] = [
          ...clients.map((c: any) => ({
            id: c.id, kind: "client" as const,
            label: c.company_name, sub: c.contact_email ?? c.contact_name ?? undefined,
            to: `/os/admin/clients/${c.id}`,
          })),
          ...projects.map((p: any) => ({
            id: p.id, kind: "project" as const,
            label: p.name, sub: p.clients?.company_name ?? undefined,
            to: `/os/admin/projects/${p.id}`,
          })),
          ...invoices.map((i: any) => ({
            id: i.id, kind: "invoice" as const,
            label: i.invoice_number ?? i.id.slice(0, 8),
            sub: i.clients?.company_name ?? undefined,
            to: `/os/admin/invoices?invoice=${i.id}`,
          })),
          ...quotes.map((qu: any) => ({
            id: qu.id, kind: "quote" as const,
            label: qu.title ?? qu.quote_number ?? qu.id.slice(0, 8),
            sub: qu.clients?.company_name ?? qu.prospect_name ?? undefined,
            to: `/os/admin/quotes/${qu.id}`,
          })),
        ];
        setHits(out);
        setLoaded(true);
      } catch {/* noop */}
    })();
  }, [open, loaded, role]);

  const navItems = useMemo(() => {
    const N = (t as any).os.nav;
    if (role === "admin") return [
      { to: "/os/admin", label: N.dashboard, icon: LayoutDashboard },
      { to: "/os/admin/clients", label: N.clients, icon: Building2 },
      { to: "/os/admin/projects", label: N.projects, icon: Briefcase },
      { to: "/os/admin/quotes", label: N.quotes, icon: FileText },
      { to: "/os/admin/invoices", label: N.invoices, icon: Receipt },
      { to: "/os/admin/users", label: N.users, icon: Users },
    ];
    if (role === "operator") return [
      { to: "/os/operator", label: N.dashboard, icon: LayoutDashboard },
      { to: "/os/operator/projects", label: N.myProjects, icon: Briefcase },
    ];
    return [
      { to: "/os/client", label: N.dashboard, icon: LayoutDashboard },
      { to: "/os/client/projects", label: N.myProjects, icon: Briefcase },
      { to: "/os/client/invoices", label: N.invoices, icon: Receipt },
    ];
  }, [role, t]);

  const go = (to: string) => {
    setOpen(false);
    setQ("");
    nav(to);
  };

  const kindIcon = (k: Hit["kind"]) => {
    const I = k === "client" ? Building2 : k === "project" ? Briefcase : k === "invoice" ? Receipt : FileText;
    return <I className="h-3.5 w-3.5 text-muted-foreground" />;
  };
  const kindLabel = (k: Hit["kind"]) =>
    k === "client" ? T.groups.clients : k === "project" ? T.groups.projects : k === "invoice" ? T.groups.invoices : T.groups.quotes;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput value={q} onValueChange={setQ} placeholder={T.placeholder} />
      <CommandList>
        <CommandEmpty>
          <div className="flex flex-col items-center gap-1.5 py-6 text-center">
            <Search className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{T.empty}</p>
          </div>
        </CommandEmpty>

        <CommandGroup heading={T.groups.navigation}>
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <CommandItem key={it.to} value={`nav ${it.label}`} onSelect={() => go(it.to)}>
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{it.label}</span>
                <ArrowRight className="ml-auto h-3 w-3 text-muted-foreground" />
              </CommandItem>
            );
          })}
        </CommandGroup>

        {role === "admin" && hits.length > 0 && (
          <>
            <CommandSeparator />
            {(["client", "project", "invoice", "quote"] as const).map((k) => {
              const group = hits.filter((h) => h.kind === k);
              if (group.length === 0) return null;
              return (
                <CommandGroup key={k} heading={kindLabel(k)}>
                  {group.slice(0, 50).map((h) => (
                    <CommandItem key={`${k}-${h.id}`} value={`${h.label} ${h.sub ?? ""}`} onSelect={() => go(h.to)}>
                      {kindIcon(k)}
                      <span className="truncate">{h.label}</span>
                      {h.sub && <span className="ml-2 text-[11px] text-muted-foreground truncate">{h.sub}</span>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading={T.groups.help}>
          <CommandItem disabled>
            <Sparkles className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">{T.tipAi}</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}

/** Small ⌘K trigger button you can drop in headers/toolbars. */
export function CommandPaletteHint({ onOpen }: { onOpen?: () => void }) {
  const { t } = useI18n();
  const T = (t as any).os.cmdk;
  return (
    <button
      type="button"
      onClick={() => {
        // Dispatch the same shortcut so the global listener opens it.
        if (onOpen) return onOpen();
        const ev = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
        window.dispatchEvent(ev);
      }}
      className="hidden md:inline-flex items-center gap-2 h-8 px-2.5 rounded-md border border-border bg-background/60 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
      aria-label={T.open}
    >
      <Search className="h-3 w-3" />
      <span>{T.open}</span>
      <kbd className="ml-2 inline-flex items-center gap-0.5 rounded border border-border px-1 py-0 text-[10px] font-mono text-muted-foreground">
        ⌘K
      </kbd>
    </button>
  );
}
